<template>
  <div>
    <!-- Sync button -->
    <sync-button
      v-bind:show="record.sync == 'cache' && isOnline"
      @handleSync="handleUpdate('cache')"
    ></sync-button>
    <!-- New content modal -->
    <div id="modal-update" class="modal">
      <div class="modal-overlay"></div>
      <div class="modal-content" role="document">
        <div class="modal-header">
          <h4 class="modal-title">Record</h4>
        </div>
        <div class="modal-body">
          <p>
            The version of this record on the server is different to the one
            <i>cached</i> on this device.
            <strong
              >The version from the
              <kbd>
                <span v-if="record.sync == 'cache'">local </span>
                {{ record.sync }}
              </kbd>
              is newer</strong
            >.
          </p>
          <p>
            Do you want to <strong>keep the local version</strong> or
            <strong>switch to the server's version</strong>?
          </p>
        </div>
        <div class="modal-footer">
          <div class="u-flex">
            <div class="level-left level-content">
              <button class="btn-info" v-on:click="handleUpdate('cache')">
                <i class="fa fa-wrapper fa-save pad-right"></i>
                Local
              </button>
            </div>
            <div class="level-right level-content">
              <button class="btn-primary" v-on:click="handleUpdate('server')">
                <i class="fa fa-wrapper fa-cloud-download-alt pad-right"></i>
                Server
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-8">
        <h4 class="font-light">Flight Details</h4>
      </div>
      <div class="col-4">
        <div class="btn-container u-text-right">
          <button
            class="btn-primary u-inline"
            v-bind:disabled="isOffline"
            v-on:click="submitRecord"
          >
            <loading-spinner v-show="saving"></loading-spinner>
            <i class="fa-wrapper fa fa-upload" v-show="!saving"></i>
            Submit
          </button>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="offset-1 col-8 faded">
        <label class="faded">
          Select this box if the task was completed previously
          and is being repeated.
        </label>
      </div>
      <div class="col-3 u-text-right" style="padding-right: 1em;">
        <div class="form-ext-control form-ext-checkbox">
          <input
            type="checkbox"
            id="repeatInput"
            class="form-ext-input"
            v-model="record.is_repeat"
            @change="updateRecord"
          >
          <label
            for="repeatInput"
            class="form-ext-label font-bold">
            Repeated task
          </label>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <table class="table bordered">
          <tbody>
            <tr>
              <th width="50%">
                Overall Result
              </th>
              <td>
                <select
                  v-model="selectedResult"
                  v-bind:class="getResultClass()"
                  @change="handleResult($event.target.value)"
                >
                  <option
                    v-for="option in recordResults"
                    v-bind:key="option.id"
                    v-bind:value="option.name"
                  >
                    {{ option.name }}
                  </option>
                </select>
                <span class="error">{{ error.result }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <table class="table bordered">
          <tbody>
            <tr>
              <th>Date</th>
              <th>{{ this.record.resource_type || "Aircraft" }}</th>
              <th>Type</th>
            </tr>
            <tr>
              <td>
                <input
                  type="date"
                  v-model="record.date"
                  @change="updateRecord"
                  v-bind:class="{ 'input-error': error.date }"
                  v-bind:max="getToday()"
                />
                <span class="error">{{ error.date }}</span>
              </td>
              <td>
                <multiselect
                  v-model="selectedAircraft"
                  :options="aircraft"
                  track-by="identifier"
                  :custom-label="getAircraftLabel"
                  :close-on-select="true"
                  @input="handleAircraft"
                  v-bind:class="{ 'input-error': error.aircraft_identifier }"
                ></multiselect>
                <span class="error">{{ error.aircraft_identifier }}</span>
              </td>
              <td>
                {{ this.record.aircraft_type }}
              </td>
            </tr>
          </tbody>
        </table>
        <div class="row error u-text-center" v-if="error.hours">
          {{ error.hours }}
        </div>
        <table class="table bordered u-no-margin">
          <tbody>
            <tr>
              <th
                v-for="(field, idx) in record.hours"
                v-bind:key="`hours-label-${idx}`"
              >
                {{ field.label | whitespace | titlecase }}
              </th>
            </tr>
            <tr>
              <td
                v-for="(field, idx) in record.hours"
                v-bind:key="`hours-input-${idx}`"
              >
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value="0.0"
                  v-model="field.value"
                  @change="updateRecord"
                  v-bind:class="{ 'input-error': error.hours }"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <div class="row">
          <div class="offset-1 col-8 faded">
            <label class="faded">
              Select this box if no flight hours were recorded for this task.
            </label>
          </div>
          <div class="col-3 u-text-right" style="padding-right: 1em;">
            <div class="form-ext-control form-ext-checkbox">
              <input
                type="checkbox"
                id="no-hours"
                class="form-ext-input"
                v-model="record.no_hours"
                @change="updateNoHours"
                >
              <label
                for="no-hours"
                class="form-ext-label font-bold">
                No Flight Hours
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="btn-container u-pull-right">
          <button
            class="btn-link btn-small"
            v-on:click="showNewItemControl()"
            v-bind:disabled="showNewItem || isOffline"
          >
            <span class="icon">
              <i
                class="fa-wrapper fa fa-plus"
                aria-hidden="true"
              ></i>
            </span>
            Add Item
          </button>
        </div>
        <h6 class="font-light">Competencies</h6>
        <!-- Create/Add Competency Item Controls -->
        <div v-if="showNewItem" class="section">
          <space></space>
          <div class="panel u-box">
            <button
              class="btn-small btn-transparent u-pull-right"
              v-on:click="closeNewItemControl()"
            >
              <i class="fa-wrapper fa fa-times"></i>
            </button>
            <label class="title">Select Competency to Add</label>
            <div class="row">
              <div class="col-10">
                <multiselect
                  v-model="newItem"
                  :options="availableItems"
                  track-by="reference"
                  :custom-label="getNewItemLabel"
                  :close-on-select="true"
                  v-bind:class="{ 'input-error': error.new_item }"
                  v-bind:disabled="isOffline"
                ></multiselect>
              </div>
              <div class="col-2">
                <button
                  class="btn-info btn-small action-button"
                  v-on:click="createItem()"
                  v-bind:disabled="isOffline"
                >
                  Add
                </button>
              </div>
            </div>
            <span class="error">{{ error.new_item }}</span>
            <div v-if="isOffline" class="toast">
              <div class="title">Offline Mode Detected</div>
              <div>You must be online in order to add items to this record.</div>
            </div>
          </div>
        </div>
        <space></space>
      </div>
    </div>
    <div class="row" v-if="error.competencies">
      <div class="col-12">
        <div class="u-box input-error error u-text-center">
          {{ error.competencies }}
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12" v-if="record.id && items.length > 0">
        <table class="table bordered striped">
          <thead>
            <tr>
              <th rowspan="2" class="u-text-left">Competency</th>
              <th v-bind:colspan="markers.length">Grade</th>
              <th class="u-hide-desktop" rowspan="2">Reason</th>
              <th class="u-hide-mobile" v-bind:colspan="reasons.length">
                Reason Codes
              </th>
            </tr>
            <tr>
              <th v-for="marker in markers" v-bind:key="`th-${marker.code}`">
                <div class="tooltip" v-bind:data-tooltip="marker.name">
                  {{ marker.code }}
                </div>
              </th>
              <th
                class="u-hide-mobile"
                v-for="reason in reasons"
                v-bind:key="`th-${reason.code}`"
              >
                {{ reason.code }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" v-bind:key="`item-${item.id}`">
              <td class="u-text-left">
                <strong>{{ item.competency_reference }}:</strong>
                {{ item.competency_name }}
                <div class="u-pull-right">
                  <button
                    v-if="item.required == false"
                    class="btn-small btn-danger outline"
                    v-on:click="destroyItem(item.id)"
                  >
                    <i class="fa-wrapper fa fa-times"></i>
                  </button>
                </div>
              </td>
              <td
                style="vertical-align: middle;"
                v-for="marker in markers"
                v-bind:key="`item-${item.id}-marker-${marker.code}`"
                class="form-ext-control form-ext-radio"
              >
                <input
                  v-bind:id="`item-${item.id}-marker-${marker.code}`"
                  v-bind:name="`item-${item.id}`"
                  class="form-ext-input"
                  v-bind:class="getMarkerClass(marker)"
                  type="radio"
                  v-bind:value="marker.code"
                  v-model="item.marker_code"
                  @click="setMarker(item, marker)"
                />
                <label
                  class="form-ext-label  form-ext-label--no-label"
                  v-bind:for="`item-${item.id}-marker-${marker.code}`"
                >
                </label>
              </td>

              <!-- Reason code controls for tablet/desktop devices -->
              <td
                v-for="(reason, idx) in reasons"
                v-show="item.can_have_reason"
                v-bind:disabled="
                  item.can_have_reason && item.requires_reason == 0
                "
                class="form-ext-control form-ext-radio u-hide-mobile"
                v-bind:class="{
                  'table-cell-error': item.reason_error,
                  first: idx == '0',
                  last: idx == reasons.length - 1
                }"
                style="vertical-align: middle;"
                v-bind:key="`item-${item.id}-reason-${reason.code}`"
              >
                <input
                  type="radio"
                  class="form-ext-input u-hide-mobile"
                  v-bind:disabled="item.requires_reason == 0"
                  v-bind:id="`item-${item.id}-reason-${reason.code}`"
                  v-bind:value="reason.code"
                  v-model="item.reason_code"
                  @click="setReason(item, $event.target.value)"
                />
                <label
                  class="form-ext-label form-ext-label--no-label"
                  v-bind:for="`item-${item.id}-reason-${reason.code}`"
                >
                </label>
              </td>

              <td class="u-hide-desktop">
                <!-- Reason code controls for mobile devices -->
                <select
                  v-if="item.can_have_reason"
                  class="select u-hide-desktop"
                  v-bind:class="{ 'input-error': item.reason_error }"
                  v-model="item.reason_code"
                  @change="setReason(item, $event.target.value)"
                >
                  <option></option>
                  <option
                    v-for="reason in reasons"
                    v-bind:key="`item-${item}-reason-${reason.code}`"
                    v-bind:value="reason.code"
                  >
                    {{ reason.code }}
                  </option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="col-12">
        <no-results-found
          title="No Competencies"
          message="Add Items to the record to have them appear here."
        >
        </no-results-found>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <h6 class="font-light">Comments</h6>
        <table class="table bordered">
          <tr>
            <td class="u-text-left">
              <span class="error">{{ error.comment }}</span>
              <textarea
                v-model="record.comment"
                @change="updateRecord"
                rows="3"
                v-bind:class="{ 'input-error': error.comment }"
                />
            </td>
          </tr>
        </table>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="row">
          <div class="btn-container u-fill-width u-text-right">
            <button
              class="btn-primary u-inline"
              v-bind:disabled="isOffline"
              v-on:click="submitRecord"
            >
              <loading-spinner v-show="saving"></loading-spinner>
              <i class="fa-wrapper fa fa-upload pad-right" v-show="!saving"></i>
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import Multiselect from "vue-multiselect";
import NoResultsFound from "@/components/PlaceholderNoResults";
import LoadingSpinner from "@/components/LoadingSpinner";
import { scrollToTop } from "@/functions/scrollToTop";
import { debounce } from "@/functions/debounce.function";
import { handleError } from "@/functions/handleError.function";
import { today } from "@/functions/dateToday.function";
import cleanReasonCode from "@/functions/cleanReasonCode.function";
import modelCache from "@/services/modelCache.service";
import SyncButton from "@/components/SyncButton";

export default {
  name: "record-complete",
  data: function() {
    return {
      saving: false,
      selectedResult: "",
      selectedAircraft: "",
      showNewItem: false,
      newItem: {},
    };
  },
  props: {
    record: {},
    items: {}
  },
  components: {
    Multiselect,
    NoResultsFound,
    LoadingSpinner,
    SyncButton
  },
  computed: {
    ...mapGetters("core", ["aircraft", "results", "error"]),
    ...mapGetters("records", ["markers", "reasons", "competencies"]),
    recordResults() {
      return this.results.filter(res => res.namespace == "records");
    },
    availableItems: function() {
      // Return the difference in Competencies included as Items on the Record
      // and the set of all Competencies.
      if (!this.items) {
        return [];
      }
      const record_item_refs = this.items.map(x => x.competency_reference);
      return this.competencies.filter(
        competency => !record_item_refs.find(ref => ref == competency.reference)
      );
    }
  },
  watch: {
    "record.result": function(newVal) {
      let result = this.results.find(obj => obj.name == newVal);
      this.selectedResult = result.name;
    },
    "record.aircraft_identifier": function(newVal) {
      this.selectedAircraft = newVal;
    },
    "record.sync": function(newVal) {
      if (newVal && this.isOnline) {
        this.showModal();
      } else {
        window.location = "#";
      }
    }
  },
  methods: {
    showModal() {
      let target = this.$route.path.split("/").pop() + "#modal-update";
      window.location = target;
    },
    showNewItemControl() {
      this.showNewItem = true;
    },
    closeNewItemControl() {
      this.showNewItem = false;
    },
    handleUpdate(value) {
      switch (value) {
        case "cache":
          // Keep the local copy, so update the server to match the cache
          var data = this.cleanRecord();
          this.$store.dispatch("records/updateRecord", data);
          for (let item of this.items) {
            item.record = this.record.id;
            this.$store.dispatch("records/updateItem", item);
          }
          break;
        case "server":
          // Update record (if it exists)
          var newRecord = this.$store.getters["records/serverData"](
            `record-${this.record.id}`
          );
          if (newRecord) {
            this.$store.commit("records/setRecord", newRecord);
          }
          // Update items stack (if it exists)
          var newItems = this.$store.getters["records/serverData"](
            `record-${this.record.id}-items`
          );
          if (newItems) {
            this.$store.commit("records/setItems", {
              id: this.record.id,
              data: newItems
            });
          }
          break;
      }
      // Clear the server data from the cache as it's no longer used/needed.
      this.$store.commit("records/removeServerData", {
        key: `record-${this.record.id}`
      });
      this.$store.commit("records/removeServerData", {
        key: `record-${this.record.id}-items`
      });
      // Close the modal dialog
      window.location = "#";
    },
    updateRecord: debounce(function() {
      // Debounce and save changes
      if (this.isOnline) {
        let data = this.cleanRecord();
        this.$store.dispatch("records/updateRecord", data);
      } else {
        // The following properties are used to manage and control the sync
        // status of the record.
        this.record.modified = new Date(Date.now()).toISOString();
        modelCache.set("records", this.record.id, this.record);
        this.record.sync = "cache"; // Display the 'sync' button
      }
    }, 1000),
    updateItem: function(item) {
      let data = Object.assign({}, item);
      data.record = this.record.id;
      if (this.isOnline) {
        this.$store.dispatch("records/updateItem", data);
      } else {
        // Update the modified timestamp and save to the cache
        data.modified = new Date(Date.now()).toISOString();
        this.$store.commit("records/setItemDetail", data);
        modelCache.set("items", this.record.id, this.items);
        this.record.sync = "cache"; // Display the 'sync' button
      }
    },
    updateNoHours: function() {
      this.$store.commit("records/setRecord", this.record);
    },
    createItem: function() {
      const data = {
        record: this.record.id,
        competency_reference: this.newItem.reference,
      }
      this.$store.dispatch("records/createItem", data);
      this.newItem = {};
    },
    destroyItem: function(idx) {
      const data = {
        record: this.record.id,
        item: idx,
      };
      this.$store.dispatch("records/destroyItem", data);

    },
    getAircraftLabel(payload) {
      if (typeof payload == "string") {
        payload = this.aircraft.find(obj => obj.identifier == payload);
      }
      return `${payload.identifier} (${payload.aircraft_type.abbreviation})`;
    },
    getNewItemLabel({reference, name}) {
      if (reference == undefined) {
        return "Select...";
      } else {
        return `${reference} - ${name}`;
      };
    },
    getResultClass() {
      if (this.error.result) {
        return "input-error";
      } else if (this.record.status == 1) {
        return "input-warning";
      } else if (this.record.status == 0) {
        return "input-success";
      }
    },
    getMarkerClass(marker) {
      if (marker.status == 1) {
        return "form-ext-input--error";
      } else if (
        marker.requires_reason == 1 || // Yes/true
        marker.requires_reason == -1 // Optional
      ) {
        return "form-ext-input--warning";
      } else if (marker.status == 0) {
        return "form-ext-input--success";
      } else {
        return "form-ext-input--dark";
      }
    },
    getItemError(item) {
      let fieldName = `item-${item.competency_reference}`;
      return this.error[fieldName];
    },
    getToday: function() {
      return today()
    },
    handleAircraft: function(value) {
      if (!value) {
        this.record.aircraft_identifier = "";
        this.record.aircraft_type = "";
        this.record.resource_type = "";
      } else {
        this.record.aircraft_identifier = value.identifier;
        this.record.aircraft_type = value.aircraft_type.abbreviation;
        this.record.resource_type = value.resource_type.name;
      }
      this.updateRecord();
    },
    handleResult: function(value) {
      this.record.result = value;
      let result = this.results.find(obj => obj.name == value);
      this.record.status = result.status;
      this.updateRecord();
    },
    setMarker(item, marker) {
      item.marker_code = marker.code;
      item.marker_name = marker.name;
      item.requires_reason = marker.requires_reason;
      item.status = marker.status;
      item = cleanReasonCode(item);
      this.updateItem(item);
    },
    setReason(item, value) {
      item.reason_code = value;
      let reason = this.reasons.find(r => r.code == value);
      if (!reason) {
        item.reason_name = "";
      } else {
        item.reason_name = reason.name;
      }
      item = cleanReasonCode(item);
      this.updateItem(item);
    },
    cleanRecord() {
      let model = Object.assign({}, this.record);
      // Remove fields we don't need to pass to the server
      delete model.candidate;
      delete model.instructor;
      delete model.candidate_signature;
      delete model.candidate_signature_dt;
      delete model.instructor_signature;
      delete model.instructor_signature_dt;
      delete model.task_code;
      delete model.task_name;
      return model;
    },
    validateRecord() {
      let isValid = true;
      // Ensure required fields are completed
      let required = ["date", "comment"];
      for (let field of required) {
        if (this.record[field] == "") {
          let err = {};
          err[field] = "This field is required";
          this.$store.commit("core/setError", err);
          isValid = false;
        }
      }
      // Must include aircraft if items are present on the record
      if (this.items.length > 0 &&
          this.record["aircraft_identifier"] == "") {
        let err = {
          aircraft_identifier: "This field is required",
        };
        this.$store.commit("core/setError", err);
        isValid = false;
      }
      // May not submit an `incomplete` result
      if (this.record.status == -1) {
        let err = {
          result: "You cannot submit the record if the result is Incomplete."
        };
        this.$store.commit("core/setError", err);
        isValid = false;
      }
      // validate hours
      let hours = 0;
      for (let field of this.record.hours) {
        hours += Number(field.value);
      }
      if (
        this.items.length > 0 &&
          this.record.hours.length > 0 &&
          this.record.no_hours == false &&
          hours == 0
      ) {
        // Make sure at least *some* hours are entered if the `no_hours` box is
        // NOT checked.
        let err = {
          hours: "At least some hours need to be entered for the flight. You can select the 'No Flight Hours' box if you didn't go flying"
        };
        this.$store.commit("core/setError", err);
        isValid = false;
      } else if (
        this.items.length > 0 &&
          this.record.hours.length > 0 &&
          this.record.no_hours == true &&
          hours > 0
      ) {
        // Make sure NO hours are entered if `no_hours` is ticked
        let err = {
          hours: "Hours are entered but the 'No Flight Hours' box is checked. Either remove the hours or uncheck the box."
        };
        this.$store.commit("core/setError", err);
        isValid = false;
      };
      // Get the info we need from the items
      let grades = new Set();
      let reason_codes = new Set();
      // Must grade at least *1* competency
      for (let item of this.items) {
        grades.add(item.status);
        item = cleanReasonCode(item);
        reason_codes.add(item.reason_error);
      }
      if (grades.size == 1 && grades.has(-1)) {
        let err = {
          competencies: "At least one competency must be graded."
        };
        this.$store.commit("core/setError", err);
        isValid = false;
      }
      // No errors allowed for reason codes
      if (reason_codes.has(true)) {
        let err = {
          reasons: "There are errors in the reason codes."
        };
        this.$store.commit("core/setError", err);
        isValid = false;
      }
      return isValid;
    },
    submitRecord() {
      let isValid = this.validateRecord();
      if (!isValid) {
        this.$store.commit("core/setError", {
          serverError: {
            label: "Form Validation Error",
            message:
              "Looks like there are problems with the form. " +
              "Please review the form and fix up the fields highlighted in red."
          }
        });
        return;
      }
      this.saving = true;
      // Save *all* of the items (in case they didn't sync properly earlier)
      let stack = [];
      for (let item of this.items) {
        let data = Object.assign({}, item);
        data.record = this.record.id;
        stack.push(this.$store.dispatch("records/updateItem", data));
      }
      return Promise.all(stack)
        .then(() => {
          // Clean the record
          let data = this.cleanRecord();
          // Close the record
          data.is_closed = true;
          this.$store
            .dispatch("records/updateRecord", data)
            .then(response => {
              // Note: The record is also updated (from the store action), so the
              // items are also updated as appropriate when this happened due to
              // the items being watched.
              scrollToTop();
              this.saving = false;
              // The success toast is handled by the parent component so that
              // it persists when the component is switched out after the record
              // is submitted.

              // Emitting this variable was awkward, so we set it directly on
              // the parent. This *isn't* ideal, but the close coupling of
              // these components probably makes it OK.
              this.$parent.success = response;
            })
            .catch(error => {
              this.saving = false;
              let data = handleError(error);
              this.$store.commit("core/setError", data);
            });
        })
        .catch(function(error) {
          this.saving = false;
          let data = handleError(error);
          this.$store.commit("core/setError", data);
        });
    }
  },
  mounted() {
    this.$store.commit("core/clearErrors");
    // Hydrate markers and reason codes.
    this.$store.commit(
      "records/setCompetencies",
      this.$offlineStorage.get("competencies")
    );
    this.$store.commit(
      "records/setMarkers",
      this.$offlineStorage.get("markers")
    );
    this.$store.commit(
      "records/setReasonCodes",
      this.$offlineStorage.get("reasons")
    );
    this.$store.commit("core/setResults", this.$offlineStorage.get("results"));
    this.$store.commit(
      "core/setAircraft",
      this.$offlineStorage.get("aircraft")
    );
    // Initialise the selected result controls
    if (this.record.id) {
      let result = this.results.find(obj => obj.name == this.record.result);
      this.selectedResult = result.name;
      // Initialise the selected aircraft
      this.selectedAircraft = this.record.aircraft_identifier;
    }
    // Show a dialog if a newer version of the record is available on the
    // server.
    setTimeout(() => {
      if (this.record.sync && this.isOnline) {
        this.showModal();
      }
    }, 500);
  }
};
</script>
<style>
.table-cell-error {
  background-color: rgba(244, 67, 54, 0.05) !important;
  border-color: var(--cirrus-danger) !important;
}
.table-cell-error:not(.first) {
  border-left: 1px solid rgba(219, 219, 219, 0.5) !important;
}
.table-cell-error:not(.last) {
  border-right: 1px solid rgba(219, 219, 219, 0.5) !important;
}
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
